// Eastern-time helpers for the client.
//
// The server stores clock times as TRUE UTC INSTANTS (a naive TIMESTAMP
// column that holds UTC wall-clock — a 2:30 PM ET punch is stored 18:30 and
// serialized to the browser as "…18:30:00Z"). The center is in Indiana
// (Eastern). The browser must NOT rely on its own timezone to read or write
// these — a director signing in from anywhere still needs the center's
// clock. Everything here pins America/New_York.

const TZ = 'America/New_York';
// Exported so lib/dates.js formats timestamps in the provider's zone without a
// second literal. When the provider's timezone becomes data, this is the seam.
export const ET_TZ = TZ;

// Minutes ET is behind UTC at a given instant (negative), DST-aware.
function etOffsetMinutes(date) {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).reduce((a, x) => (a[x.type] = x.value, a), {});
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, p.hour === '24' ? 0 : +p.hour, +p.minute, +p.second);
  return (asUTC - date.getTime()) / 60000;
}

// A datetime-local string ("YYYY-MM-DDTHH:MM"), typed as Eastern wall-clock,
// → the true UTC instant (ISO) to store. Independent of the browser's TZ.
export function etLocalInputToInstant(s) {
  if (!s) return null;
  const [d, t] = s.split('T');
  const [Y, M, D] = d.split('-').map(Number);
  const [h, m] = (t || '00:00').split(':').map(Number);
  const asUTC = Date.UTC(Y, M - 1, D, h, m, 0);
  const off = etOffsetMinutes(new Date(asUTC));
  return new Date(asUTC - off * 60000).toISOString();
}

// A true UTC instant (ISO string or Date) → "YYYY-MM-DDTHH:MM" in Eastern,
// for seeding a <input type="datetime-local">.
export function instantToEtLocalInput(iso) {
  if (!iso) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date(iso)).reduce((a, x) => (a[x.type] = x.value, a), {});
  const hh = p.hour === '24' ? '00' : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hh}:${p.minute}`;
}

// Today's calendar date in Eastern as "YYYY-MM-DD", independent of where the
// browser is. Use this anywhere a date is sent to the server as a business
// day — building it from new Date().getFullYear()/getMonth()/getDate() reads
// the VIEWER's timezone, which near midnight (or from another zone) names the
// wrong day.
export function etTodayISO(d = new Date()) {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d).reduce((a, x) => (a[x.type] = x.value, a), {});
  return `${p.year}-${p.month}-${p.day}`;
}

// Display formatters — always Eastern.
export function formatEtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}
export function formatEtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
}
export function formatEtDayLabel(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { timeZone: TZ, weekday: 'long', month: 'short', day: 'numeric' });
}
